class RadarPlaybackEngine {
  constructor(options = {}) {
    this._frameIntervalMs = options.frameIntervalMs ?? 600;
    this._frames = [];
    this._index = 0;
    this._playing = false;
    this._timer = null;
    this._listeners = {};
  }

  // --- Public API ---

  loadFrames(frames) {
    this._frames = Array.isArray(frames) ? frames : [];
    this._index = 0; // reset before pause() so stateChange carries index 0, not the old index
    this.pause();
    this._emit('frameChange', { frame: this.currentFrame, index: this._index, frameCount: this.frameCount });
    this._emit('stateChange', { playing: this._playing, index: this._index, frameCount: this.frameCount });
  }

  // Called on manifest poll updates — preserves playback position and playing state.
  // Use loadFrames() instead when switching site or product (hard reset).
  updateFrames(frames) {
    const oldCount = this._frames.length;
    this._frames = Array.isArray(frames) ? frames : [];
    if (this._index >= this._frames.length) {
      this._index = Math.max(0, this._frames.length - 1);
    }
    this._emit('frameChange', { frame: this.currentFrame, index: this._index, frameCount: this.frameCount });
    if (this._frames.length !== oldCount) {
      this._emit('stateChange', { playing: this._playing, index: this._index, frameCount: this.frameCount });
    }
  }

  play() {
    if (this._playing || this._frames.length === 0) return;
    this._playing = true;
    this._timer = setInterval(() => {
      this._index = (this._index + 1) % this._frames.length;
      this._emit('frameChange', { frame: this.currentFrame, index: this._index, frameCount: this.frameCount });
    }, this._frameIntervalMs);
    this._emit('stateChange', { playing: this._playing, index: this._index, frameCount: this.frameCount });
  }

  pause() {
    if (!this._playing) return;
    clearInterval(this._timer);
    this._timer = null;
    this._playing = false;
    this._emit('stateChange', { playing: this._playing, index: this._index, frameCount: this.frameCount });
  }

  seek(index) {
    if (this._frames.length === 0) return;
    this._index = Math.max(0, Math.min(this._frames.length - 1, index));
    this._emit('frameChange', { frame: this.currentFrame, index: this._index, frameCount: this.frameCount });
  }

  next() {
    this.pause();
    this.seek(this._index + 1); // seek() clamps — no wrap
  }

  prev() {
    this.pause();
    this.seek(this._index - 1); // seek() clamps — no wrap
  }

  jumpToLatest() {
    this.seek(this._frames.length - 1);
  }

  destroy() {
    clearInterval(this._timer);
    this._timer = null;
    this._listeners = {};
  }

  // --- Getters ---

  get currentFrame() {
    return this._frames[this._index] ?? null;
  }

  get currentIndex() {
    return this._index;
  }

  get frameCount() {
    return this._frames.length;
  }

  get isPlaying() {
    return this._playing;
  }

  // --- Event system ---

  on(event, listener) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(listener);
    return () => this.off(event, listener);
  }

  off(event, listener) {
    if (!this._listeners[event]) return;
    const i = this._listeners[event].indexOf(listener);
    if (i !== -1) this._listeners[event].splice(i, 1);
  }

  _emit(event, payload) {
    (this._listeners[event] ?? []).forEach(fn => fn(payload));
  }
}

export default RadarPlaybackEngine;
